import { useEffect, useRef } from "react";
import { hexToRgb } from "./canvasUtils";

/**
 * useFxCompositor — runtime chromakey + virtual background for an outbound
 * MediaStream track.
 *
 * When `active` is true and there is a source video track on
 * `getSourceStream()`, we:
 *   1. Pipe the track into a hidden <video>
 *   2. Composite (background + chromakey'd video) onto a hidden <canvas>
 *   3. Produce a new MediaStreamTrack via `canvas.captureStream()`
 *   4. Call `onCompositedTrack(track)` so the caller can replaceTrack() on all
 *      PeerConnections and update the local preview.
 *
 * When `active` flips back to false, we call `onCompositedTrack(null)` so the
 * caller can restore the original camera track.
 *
 * This runs entirely on the client — no server FX required.
 */
export function useFxCompositor({
  active,
  settings,
  getSourceStream,
  onCompositedTrack,
  width = 1280,
  height = 720,
}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const bgImgRef = useRef(null);
  const bgVideoRef = useRef(null); // hidden <video> for animated backgrounds
  const rafRef = useRef(null);
  const producedTrackRef = useRef(null);
  const producedStreamRef = useRef(null);

  // keep the latest settings in a ref so we don't restart the raf loop on every slider tweak
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  useEffect(() => {
    if (!active) {
      // teardown
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      try { producedStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
      producedStreamRef.current = null;
      producedTrackRef.current = null;
      try { onCompositedTrack?.(null); } catch {}
      if (typeof window !== "undefined" && window.__TAPIN_FX_DEBUG) console.log("[fx-compositor] inactive — teardown");
      return;
    }

    const src = getSourceStream?.();
    const videoTrack = src?.getVideoTracks?.()[0];
    if (typeof window !== "undefined" && window.__TAPIN_FX_DEBUG) {
      console.log("[fx-compositor] activating", {
        hasSrc: !!src,
        videoTrackLabel: videoTrack?.label,
        videoTrackReadyState: videoTrack?.readyState,
        chromaEnabled: settingsRef.current?.chroma?.enabled,
        bgMode: settingsRef.current?.background?.mode,
        bgHasSource: !!(settingsRef.current?.background?.dataUrl || settingsRef.current?.background?.url),
      });
    }
    if (!videoTrack) {
      // nothing to composite yet — bail; caller will retry when the camera track appears
      try { onCompositedTrack?.(null); } catch {}
      if (typeof window !== "undefined" && window.__TAPIN_FX_DEBUG) console.warn("[fx-compositor] no video track on source — bailing");
      return;
    }

    // Build hidden video + canvas once per activation.
    // ⚠ Firefox + some Chrome builds only produce video frames when the
    // <video> element is attached to the document. Add it to a hidden,
    // off-screen container so drawImage(v, ...) reliably delivers frames.
    const v = document.createElement("video");
    v.srcObject = new MediaStream([videoTrack]);
    v.muted = true;
    v.playsInline = true;
    v.autoplay = true;
    v.style.position = "fixed";
    v.style.left = "-9999px";
    v.style.top = "0";
    v.style.width = "1px";
    v.style.height = "1px";
    v.style.opacity = "0";
    v.setAttribute("aria-hidden", "true");
    document.body.appendChild(v);
    videoRef.current = v;
    v.play().catch(() => {});

    const c = document.createElement("canvas");
    c.width = width;
    c.height = height;
    canvasRef.current = c;
    const ctx = c.getContext("2d", { willReadFrequently: true });

    // Paint one black frame up front so `canvas.captureStream()` starts
    // with an active (non-muted) video track — prevents the viewer
    // seeing a frozen / blank feed during the ~100–300ms it takes for
    // the <video> element to reach readyState >= 2.
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, c.width, c.height);

    // Background image element for image BG mode (create BEFORE tick so
    // the first frame after the img loads is already correct).
    const img = document.createElement("img");
    img.crossOrigin = "anonymous";
    img.src = settingsRef.current?.background?.dataUrl || settingsRef.current?.background?.url || "";
    bgImgRef.current = img;

    // Background <video> element for video BG mode (looped, muted,
    // off-screen). Source can be a data URL (admin-uploaded ≤10MB) or a
    // remote URL (admin-curated link). Like the main camera <video>,
    // Firefox and some mobile Chromes only produce drawable frames when
    // the element is attached to the DOM — so we slip it next to the
    // hidden camera <video>. crossOrigin='anonymous' is required so the
    // canvas stays readable (`getImageData` inside the chroma pass).
    const bgV = document.createElement("video");
    bgV.crossOrigin = "anonymous";
    bgV.muted = true;
    bgV.loop = true;
    bgV.playsInline = true;
    bgV.autoplay = true;
    bgV.style.position = "fixed";
    bgV.style.left = "-9999px";
    bgV.style.top = "0";
    bgV.style.width = "1px";
    bgV.style.height = "1px";
    bgV.style.opacity = "0";
    bgV.setAttribute("aria-hidden", "true");
    const bgSrc = settingsRef.current?.background?.mode === "video"
      ? (settingsRef.current?.background?.dataUrl || settingsRef.current?.background?.url || "")
      : "";
    if (bgSrc) {
      bgV.src = bgSrc;
      document.body.appendChild(bgV);
      bgV.play().catch(() => {});
    }
    bgVideoRef.current = bgV;

    // Hot-swap the bg video source if the host changes backgrounds mid-
    // stream — watch a ref and update src/attach on change.
    let lastBgKey = `${settingsRef.current?.background?.mode}|${bgSrc}`;

    const tick = () => {
      const s = settingsRef.current || {};
      // Hot-swap background source when the host picks a new one —
      // lastBgKey guards so we don't rebuild the <video> every frame.
      const curSrc = s.background?.dataUrl || s.background?.url || "";
      const curKey = `${s.background?.mode}|${curSrc}`;
      if (curKey !== lastBgKey) {
        lastBgKey = curKey;
        if (s.background?.mode === "image") {
          if (curSrc && bgImgRef.current) bgImgRef.current.src = curSrc;
        } else if (s.background?.mode === "video") {
          if (bgVideoRef.current) {
            const bv = bgVideoRef.current;
            if (curSrc && bv.src !== curSrc) {
              bv.src = curSrc;
              if (!bv.isConnected) document.body.appendChild(bv);
              bv.play().catch(() => {});
            }
          }
        } else {
          // blur/none — tear down the bg video if it was attached
          if (bgVideoRef.current?.isConnected) {
            try { bgVideoRef.current.pause(); } catch {}
            try { bgVideoRef.current.remove(); } catch {}
          }
        }
      }

      if (v.readyState >= 2) {
        // 1) Background
        if (s.background?.mode === "image" && bgImgRef.current?.complete && bgImgRef.current.naturalWidth > 0) {
          ctx.drawImage(bgImgRef.current, 0, 0, c.width, c.height);
        } else if (s.background?.mode === "video" && bgVideoRef.current?.readyState >= 2) {
          // Cover-fit the background video so portrait / landscape source
          // clips fill the 16:9 canvas without letterboxing.
          const bv = bgVideoRef.current;
          const srcAR = (bv.videoWidth || c.width) / (bv.videoHeight || c.height);
          const dstAR = c.width / c.height;
          let dw = c.width, dh = c.height, dx = 0, dy = 0;
          if (srcAR > dstAR) {
            dw = c.height * srcAR;
            dx = (c.width - dw) / 2;
          } else {
            dh = c.width / srcAR;
            dy = (c.height - dh) / 2;
          }
          ctx.drawImage(bv, dx, dy, dw, dh);
        } else if (s.background?.mode === "blur") {
          ctx.filter = "blur(20px) brightness(0.7)";
          ctx.drawImage(v, -10, -10, c.width + 20, c.height + 20);
          ctx.filter = "none";
        } else {
          ctx.clearRect(0, 0, c.width, c.height);
          ctx.fillStyle = "#000";
          ctx.fillRect(0, 0, c.width, c.height);
        }

        // 2) Video with optional chromakey
        if (!s.chroma?.enabled) {
          ctx.drawImage(v, 0, 0, c.width, c.height);
        } else {
          const off = document.createElement("canvas");
          off.width = c.width;
          off.height = c.height;
          const octx = off.getContext("2d", { willReadFrequently: true });
          octx.drawImage(v, 0, 0, c.width, c.height);
          const frame = octx.getImageData(0, 0, c.width, c.height);
          const data = frame.data;
          const key = hexToRgb(s.chroma.color || "#00FF00");
          const sim = (s.chroma.similarity ?? 0.4) * 255;
          const smoothPx = Math.max(1, sim * ((s.chroma.smoothness ?? 0.1) * 2));
          for (let i = 0; i < data.length; i += 4) {
            const dr = data[i] - key.r;
            const dg = data[i + 1] - key.g;
            const db = data[i + 2] - key.b;
            const dist = Math.sqrt(dr * dr + dg * dg + db * db);
            if (dist < sim) {
              data[i + 3] = 0;
            } else if (dist < sim + smoothPx) {
              data[i + 3] = Math.round(((dist - sim) / smoothPx) * 255);
            }
          }
          octx.putImageData(frame, 0, 0);
          ctx.drawImage(off, 0, 0);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    tick();

    // Capture output stream from the canvas at 30fps (most browsers cap here anyway).
    const producedStream = c.captureStream(30);
    producedStreamRef.current = producedStream;
    const producedTrack = producedStream.getVideoTracks()[0];
    // Tell the encoder this is motion content — otherwise Chrome picks
    // "detail" for canvas-captured streams which drops framerate to save
    // bits and viewers see a stuttery / frozen feed. MUST be set before
    // the sender starts encoding.
    try { if (producedTrack) producedTrack.contentHint = "motion"; } catch {}
    producedTrackRef.current = producedTrack;

    // Wait for the underlying <video> to actually have a frame before
    // swapping the track onto live PeerConnections. Without this gate
    // viewers would receive the canvas track while it's still only
    // showing our black priming frame — so "background is removed but
    // nothing shows on the stream".
    let delivered = false;
    const deliver = () => {
      if (delivered) return;
      delivered = true;
      try { onCompositedTrack?.(producedTrack); } catch {}
    };
    if (v.readyState >= 2) {
      deliver();
    } else {
      const onReady = () => { v.removeEventListener("loadeddata", onReady); deliver(); };
      v.addEventListener("loadeddata", onReady);
      // Hard safety net — if the event never fires (some devices emit
      // `playing` first), deliver after 600ms anyway so the viewer at
      // worst sees a 0.6s black-frame prefix rather than no video at all.
      setTimeout(deliver, 600);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
      try { producedStream.getTracks().forEach((t) => t.stop()); } catch {}
      producedStreamRef.current = null;
      producedTrackRef.current = null;
      try { v.srcObject = null; } catch {}
      try { v.remove(); } catch {}
      try { bgVideoRef.current?.pause(); } catch {}
      try { bgVideoRef.current?.remove(); } catch {}
      bgVideoRef.current = null;
    };
    // eslint-disable-next-line
  }, [active, width, height, getSourceStream]);

  // Re-load the background image when the data url changes while active
  useEffect(() => {
    if (!active) return;
    const url = settings?.background?.dataUrl;
    if (!url || !bgImgRef.current) return;
    if (bgImgRef.current.src !== url) bgImgRef.current.src = url;
  }, [active, settings?.background?.dataUrl]);
}
